import { ReactNode, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { useParams } from "react-router";
import { Box, Heading, Tab, TabList, TabPanel, TabPanels, Tabs, Tooltip } from "@chakra-ui/react";
import CustomBox from "../../components/Box";
import Button from "../../components/Button";
import Modal from "../../components/Modal";
import downloadPDF from "../../api/downloadPDF";
import { Extraction as ExtractionType } from "../../api/types";
import useGetExtraction from "../../api/useGetExtraction";
import useEntitySelection from "../../hooks/useEntitySelection";
import useWebSocketMessage from "../../hooks/useWebSocketMessage";
import { typedSxMap } from "../../utils/typedSxMap";
import { getStatusBadge } from "../pipelineImports/utils/pipelineImportsTable";
import OverviewSection from "./components/OverviewSection";
import PDFData from "./components/PDFData";
import CompleteExtractionForm from "./components/completeExtractionForm";
import { breakpoints } from "./components/utils/breakpoints";

enum TABS_PDF {
  OVERVIEW = 0,
  DATA = 1,
}

const styles = typedSxMap({
  container: {
    flexGrow: 1,
    height: "calc(100% + var(--m))",
    display: "flex",
    flexDirection: "column",
    marginBottom: "-30px",
  },
  imageContainer: {
    position: "relative",
    flex: 2,
  },
  divider: {
    mt: 4,
    mb: 2,
    borderColor: "var(--color-border)",
    borderBottomWidth: "1px",
    borderStyle: "solid",
    marginBottom: 0,
  },
  title: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "var(--m-xs)",
    [breakpoints.md]: {
      flexDirection: "row",
      gap: "var(--m-xs)",
    },
  },
  mainText: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
    width: "100%",
    "& span": {
      maxWidth: "calc(100% - 81px)",
    },
    [breakpoints.md]: {
      width: "calc(100% - 435px)",
    },
  },
  headingTitle: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  mainActions: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flex: 1,
    overflow: "auto",
    justifyContent: "flex-end",
    alignSelf: "flex-end",
  },
  headingWrapper: {
    marginBottom: "1rem",
  },
  titleWithOpenSidebar: {
    flexDirection: "column",
    gap: "var(--m)",
    alignItems: "flex-start",

    "& > :last-child": {
      alignSelf: "flex-end",
    },
  },
  tableContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    "&>div:first-of-type": {
      margin: "0 0 var(--m) 0",
    },
  },

  tooltipWrapper: {
    backgroundColor: "var(--color-background-modal)",
    padding: "var(--m-xxs) var(--m-xs)",
    borderRadius: "var(--border-radius)",
    marginTop: "var(--m-xs)",
    boxShadow: "0 0 0 1px var(--color-border), 0 5px 15px rgba(0, 0, 0, 0.2)",
    maxWidth: "300px",
    color: "var(--color-text)",
  },
  mappingModal: {
    minHeight: "40rem",
    minWidth: "30rem",
    "& h2": {
      marginBottom: "40px",
    },
  },
  tabs: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  tabPanels: {
    flexGrow: 1,
  },
  tabPanel: {
    height: "100%",
    padding: "var(--m-s) 0 0 0",
  },
  pdfTabPanel: {
    padding: "var(--m-s) 0 0 0",
  },

  tabList: {
    borderBottom: "1px solid var(--color-border)",
    "& button": {
      color: "var(--color-text-soft)",
      padding: "var(--m-xxs) var(--m-s)",
      "&:hover": {
        color: "var(--color-text)",
        borderColor: "var(--color-primary)",
      },
      "&[aria-selected=true]": {
        color: "var(--color-text)",
        borderColor: "var(--color-primary)",
        backgroundColor: "transparent",
      },
    },
  },
});

export default function Extraction() {
  const queryClient = useQueryClient();
  const message = useWebSocketMessage();
  const { extractionId } = useParams();
  const { data: extraction, isLoading, refetch } = useGetExtraction(extractionId ?? "");
  const [selectedTabPDF, setSelectedTabPDF] = useState<TABS_PDF>(TABS_PDF.OVERVIEW);
  const { modal } = useEntitySelection();
  const modalContent = useMemo(
    () => (
      <CustomBox>
        <CompleteExtractionForm
          extraction={extraction}
          onSuccess={() => {
            setSelectedTabPDF(TABS_PDF.OVERVIEW);
            modal.handleClose();
            // navigate("/extractions");
          }}
          onClose={modal.handleClose}></CompleteExtractionForm>
      </CustomBox>
    ),
    [modal.openDelayed]
  );

  useEffect(() => {
    const fetchData = async () => {
      if (message?.data === "refresh") {
        await queryClient.invalidateQueries(["extraction", extraction?.id]);
      }
    };
    fetchData();
  }, [message, queryClient]);

  const getTitle = (extraction?: ExtractionType) => {
    return `${extraction?.name} - Extraction`;
  };

  const importTitle: ReactNode = (
    <Heading size="m" sx={styles.headingTitle} as="h1">
      {getTitle(extraction)}
    </Heading>
  );

  const downloadPdf = (extraction?: ExtractionType) => {
    let fileName = `${extraction?.id}.pdf`;
    downloadPDF(extraction?.id || "", fileName);
  };

  if (isLoading) {
    return null;
  }

  return (
    <>
      <Box sx={styles.container}>
        <Box sx={styles.headingWrapper}>
          <Box
            sx={{
              ...styles.title,
            }}>
            <Box sx={styles.mainText}>
              {getTitle(extraction).length > 50 ? (
                <Tooltip label={getTitle(extraction)} shouldWrapChildren={true} sx={styles.tooltipWrapper}>
                  {importTitle}
                </Tooltip>
              ) : (
                <>{importTitle}</>
              )}
              {getStatusBadge(extraction?.status)}
            </Box>
            <Box sx={styles.mainActions}>
              <Button variants={["secondary"]} onClick={() => downloadPdf(extraction)}>
                Download PDF
              </Button>
              <Button
                variants={["primary"]}
                onClick={() => modal.setOpen(true)}
                disabled={isLoading || extraction?.status === "importing" || extraction?.status === "complete"}>
                Complete Review
              </Button>
            </Box>
          </Box>
        </Box>
        <Tabs
          sx={styles.tabs}
          index={selectedTabPDF}
          onChange={(tabIndex) => {
            setSelectedTabPDF(tabIndex as TABS_PDF);
          }}>
          <TabList sx={styles.tabList}>
            <Tab>Overview</Tab>
            <Tab>Data</Tab>
          </TabList>
          <TabPanels sx={styles.tabPanels}>
            <TabPanel sx={styles.tabPanel}>{extraction && <OverviewSection extraction={extraction} />}</TabPanel>
            <TabPanel sx={styles.pdfTabPanel}>
              <PDFData extraction={extraction}></PDFData>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
      {modal.openDelayed && (
        <Modal {...modal} useBox={false} useCloseButton>
          {modalContent}
        </Modal>
      )}
    </>
  );
}
